import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  getSalesOrders,
  getSalesOrder,
  addSalesOrder,
  getCustomers,
  getItemsWNWOStock,
  getNextSONumber,
  cancelSalesOrder,
  uploadSalesOrderAttachment,
  getSalesOrderAttachments,
  deleteSalesOrderAttachment,
  approveSalesOrder,
  disapproveSalesOrder,
  savePendingApprovalRemarks
} from "../../services/apiService";

import "./SalesOrders.css";
import SoPrintTemplate from "./SoPrintTemplate";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";

const RESTRICTED_SO_ROLES = [1, 2, 9, 10, 11, 16, 17];

const getCurrentUser = () => {
  try {
    return JSON.parse(
      localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
    );
  } catch {
    return {};
  }
};

const getRoleId = () => {
  const user = getCurrentUser();

  return Number(
    user.RoleID ??
    user.roleId ??
    user.RoleId ??
    user.roleID ??
    user.role_id ??
    0
  );
};

const getCurrentUserId = () => {
  const user = getCurrentUser();

  return Number(
    user.UserID ??
    user.userId ??
    user.UserId ??
    user.userID ??
    user.id ??
    user.ID ??
    0
  );
};

const getCurrentUserName = () => {
  const user = getCurrentUser();

  return String(
    user.FullName ??
    user.fullName ??
    user.Username ??
    user.username ??
    user.UserName ??
    user.Name ??
    user.name ??
    ""
  ).trim();
};

const canOnlySeeOwnSO = (roleId) => {
  return RESTRICTED_SO_ROLES.includes(Number(roleId));
};

const isOwnSalesOrder = (so, currentUserId, currentUserName) => {
  const createdById = Number(
    so.CreatedByID ??
    so.createdbyid ??
    so.CreatedById ??
    so.createdById ??
    so.created_by_id ??
    0
  );

  const preparedBy = String(
    so.PreparedBy ??
    so.preparedby ??
    so.CreatedBy ??
    so.createdby ??
    so.CreatedByName ??
    so.createdbyname ??
    ""
  ).trim();

  if (createdById && currentUserId && createdById === currentUserId) {
    return true;
  }

  if (
    preparedBy &&
    currentUserName &&
    preparedBy.toLowerCase() === currentUserName.toLowerCase()
  ) {
    return true;
  }

  return false;
};

const filterSalesOrdersByRole = (orders, roleId) => {
  const list = Array.isArray(orders) ? orders : [];

  if (!canOnlySeeOwnSO(roleId)) {
    return list;
  }

  const currentUserId = getCurrentUserId();
  const currentUserName = getCurrentUserName();

  if (!currentUserId && !currentUserName) {
    return list;
  }

  return list.filter((so) => isOwnSalesOrder(so, currentUserId, currentUserName));
};

const normalizeFilterValue = (value) => {
  return String(value ?? "").trim().toUpperCase();
};

const toDateTimeLocalInput = (value = new Date()) => {
  const d = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const toMySQLDateTime = (value) => {
  if (!value) return null;

  // from datetime-local: 2026-05-08T13:45
  if (String(value).includes("T")) {
    return `${String(value).replace("T", " ")}:00`;
  }

  return value;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

const toDateOnly = (value) => {
  if (!value) return "";

  const raw = String(value).trim();

  // Handles MySQL date/datetime values like: 2026-05-08 or 2026-05-08 13:45:00
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const cleanDecimal = (value) => {
  const cleaned = String(value ?? "")
    .replace(/,/g, "")
    .trim();

  const number = Number(cleaned);

  return Number.isFinite(number) ? number : 0;
};

const formatMoney = (value) => {
  return cleanDecimal(value).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export default function SalesOrders() {
  const [customers, setCustomers] = useState([]);
  const [itemsCatalog, setItemsCatalog] = useState([]);
  const [soList, setSoList] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showApprovalConfirmModal, setShowApprovalConfirmModal] =
    useState(false);

  const [editItemIndex, setEditItemIndex] = useState(null);
  const [selectedSO, setSelectedSO] = useState(null);

  const [catalogSearch, setCatalogSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");

  const [itemError, setItemError] = useState("");
  const [saveError, setSaveError] = useState("");

  const [attachmentSizeModal, setAttachmentSizeModal] = useState({
    show: false,
    files: [],
  });

  const [cancelError, setCancelError] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalError, setApprovalError] = useState("");
  const [approvalRemarksInput, setApprovalRemarksInput] = useState("");
  const [pendingApprovalAction, setPendingApprovalAction] = useState("");

  const [showStockPopup, setShowStockPopup] = useState(false);
  // const [stockPopupMessage, setStockPopupMessage] = useState("");

  const [tableSearch, setTableSearch] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("ALL");
  const [createdDateFrom, setCreatedDateFrom] = useState("");
  const [createdDateTo, setCreatedDateTo] = useState("");
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("ALL");
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState("ALL");
  const [orderTypeFilter, setOrderTypeFilter] = useState("ALL");
  const [deliveryModeFilter, setDeliveryModeFilter] = useState("ALL");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [soFiles, setSoFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const [showDeleteAttachmentConfirm, setShowDeleteAttachmentConfirm] =
    useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [attachmentDeleteLoading, setAttachmentDeleteLoading] = useState(false);
  const [attachmentDeleteError, setAttachmentDeleteError] = useState("");

  const [showCustomerDetailsModal, setShowCustomerDetailsModal] = useState(false);
  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(null);

  const initialCatalogRef = useRef([]);
  const printRef = useRef(null);

  const [isSavingSO, setIsSavingSO] = useState(false);
  const savingSORef = useRef(false);

  const customerDropdownRef = useRef(null);

  const userRoleId = useMemo(() => getRoleId(), []);
  const isRestrictedSOView = useMemo(
    () => canOnlySeeOwnSO(userRoleId),
    [userRoleId]
  );

  const canReviewApproval = [8, 10, 11, 12, 13, 16].includes(Number(userRoleId));
  const canCreateSalesOrder = userRoleId !== 8;
  const canCancelSalesOrder = userRoleId !== 8;



  const MAX_ATTACHMENT_SIZE_KB = 1024;
  const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_KB * 1024;

  const formatFileSize = (bytes = 0) => {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }

    return `${Math.ceil(bytes / 1024)} KB`;
  };

  const openAttachmentSizeModal = (files = []) => {
    setAttachmentSizeModal({
      show: true,
      files: files.map((file) => ({
        name: file.name,
        size: formatFileSize(file.size),
      })),
    });
  };

  const closeAttachmentSizeModal = () => {
    setAttachmentSizeModal({
      show: false,
      files: [],
    });
  };

  const emptyHeader = {
    so_number: "",
    sold_to: "",
    ship_to: "",
    mark_to: "",
    customer_number: "",
    reference_order: "",
    date: toDateTimeLocalInput(),
    shipping_instruction: "",
    special_instruction: "",
    terms_of_payment: "",
    vat_option: "yes",
    order_type: "NORMAL",
    delivery_mode: "SINGLE",
    delivery_status: "PENDING",
    stagger_deliveries: "2",
    stagger_frequency: "MONTHLY",
    stagger_start_month: new Date().toISOString().slice(0, 7)
  };

  const [header, setHeader] = useState(emptyHeader);

  const emptyItem = {
    ItemId: "",
    catalog_number: "",
    item_name: "",
    unit_of_measure: "",
    quantity: "",
    unit_price: "",
    extension_price: ""
  };

  const [itemForm, setItemForm] = useState(emptyItem);
  const [detailItems, setDetailItems] = useState([]);

  const normalizeSOForView = (fullSO, prev = null) => ({
    ...fullSO,
    vat_option: String(
      fullSO?.VATOption || prev?.vat_option || "yes"
    ).toLowerCase(),
    SOStatus:
      fullSO?.SOStatus || fullSO?.sostatus || prev?.SOStatus || "PENDING",
    ApprovalStatus:
      fullSO?.ApprovalStatus ||
      fullSO?.approvalstatus ||
      prev?.ApprovalStatus ||
      "PENDING",
    ApprovedBy:
      fullSO?.ApprovedBy || fullSO?.approvedby || prev?.ApprovedBy || null,
    ApprovedAt:
      fullSO?.ApprovedAt || fullSO?.approvedat || prev?.ApprovedAt || null,
    DisapprovedBy:
      fullSO?.DisapprovedBy ||
      fullSO?.disapprovedby ||
      prev?.DisapprovedBy ||
      null,
    DisapprovedAt:
      fullSO?.DisapprovedAt ||
      fullSO?.disapprovedat ||
      prev?.DisapprovedAt ||
      null,
    ApprovalRemarks:
      fullSO?.ApprovalRemarks ||
      fullSO?.approvalremarks ||
      prev?.ApprovalRemarks ||
      prev?.approvalremarks ||
      null,
    PendingRemarks:
      fullSO?.PendingRemarks ||
      fullSO?.pendingremarks ||
      prev?.PendingRemarks ||
      prev?.pendingremarks ||
      null,
    PendingBy:
      fullSO?.PendingBy ||
      fullSO?.pendingby ||
      prev?.PendingBy ||
      prev?.pendingby ||
      null,
    PendingAt:
      fullSO?.PendingAt ||
      fullSO?.pendingat ||
      prev?.PendingAt ||
      prev?.pendingat ||
      null,
    SpecialInstruction:
      fullSO?.SpecialInstruction ||
      fullSO?.special_instruction ||
      fullSO?.specialinstruction ||
      prev?.SpecialInstruction ||
      prev?.special_instruction ||
      null,
    ReferenceOrder:
      fullSO?.ReferenceOrder ||
      fullSO?.referenceorder ||
      fullSO?.reference_order ||
      prev?.ReferenceOrder ||
      prev?.referenceorder ||
      prev?.reference_order ||
      null,
    PreparedBy:
      fullSO?.PreparedBy || fullSO?.preparedby || prev?.PreparedBy || null,
    CreatedByID:
      fullSO?.CreatedByID || fullSO?.createdbyid || prev?.CreatedByID || null,
    CreatedByName:
      fullSO?.CreatedByName ||
      fullSO?.createdbyname ||
      fullSO?.CreatedBy ||
      fullSO?.createdby ||
      fullSO?.PreparedBy ||
      fullSO?.preparedby ||
      prev?.CreatedByName ||
      prev?.createdbyname ||
      prev?.CreatedBy ||
      prev?.createdby ||
      prev?.PreparedBy ||
      prev?.preparedby ||
      null,
    stagger_deliveries:
      fullSO?.stagger_deliveries ??
      fullSO?.StaggerDeliveries ??
      prev?.stagger_deliveries ??
      null,
    stagger_frequency:
      fullSO?.stagger_frequency ??
      fullSO?.StaggerFrequency ??
      prev?.stagger_frequency ??
      null,
    stagger_start_month:
      fullSO?.stagger_start_month ??
      fullSO?.StaggerStartMonth ??
      prev?.stagger_start_month ??
      null
  });

  const loadData = async () => {
    const c = await getCustomers();
    const catalog = await getItemsWNWOStock();
    const so = await getSalesOrders();

    setCustomers(c || []);
    setItemsCatalog(catalog || []);
    initialCatalogRef.current = catalog || [];
    setSoList(filterSalesOrdersByRole(so || [], userRoleId));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let timerId;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;

      try {
        const [so, catalog] = await Promise.all([
          getSalesOrders(),
          getItemsWNWOStock()
        ]);

        if (cancelled) return;

        setSoList(filterSalesOrdersByRole(so || [], userRoleId));
        setItemsCatalog(catalog || []);
        initialCatalogRef.current = catalog || [];

        if (selectedSO) {
          const soid = selectedSO.SOID || selectedSO.soid;

          if (soid) {
            const updated = await getSalesOrder(soid);

            if (!cancelled && updated) {
              setSelectedSO((prev) => normalizeSOForView(updated, prev));
            }
          }
        }
      } catch (e) {
        console.error("SalesOrders poll error:", e);
      } finally {
        if (!cancelled) timerId = setTimeout(poll, 10000);
      }
    };

    timerId = setTimeout(poll, 10000);

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [selectedSO, userRoleId]);

  const getCustomerId = (c) =>
    c?.customer_id ?? c?.CustomerID ?? c?.CustomerId ?? c?.id ?? "";

  const getCustomerName = (c) =>
    c?.customer_name ?? c?.CustomerName ?? c?.customername ?? "";

  const getCustomerNumber = (c) =>
    c?.customer_number ?? c?.CustomerNumber ?? c?.Customer_Number ?? "";

  const normalizeText = (value) =>
    String(value ?? "").trim().toLowerCase();

  // const handleSelectCustomer = (customer_id) => {
  //   const c = customers.find((x) => String(x.customer_id) === String(customer_id));

  //   setHeader((prev) => ({
  //     ...prev,
  //     sold_to: customer_id,
  //     ship_to: c?.ship_to || "",
  //     customer_number: c?.customer_number || "",
  //     terms_of_payment: c?.terms || ""
  //   }));
  // };

  const handleSelectCustomer = (customer_id) => {
    const id = String(customer_id || "").trim();

    if (!id) {
      setHeader((prev) => ({
        ...prev,
        sold_to: "",
        ship_to: "",
        customer_number: "",
        terms_of_payment: ""
      }));

      setCustomerSearch("");
      setShowCustomerDropdown(false);
      return;
    }

    const c = customers.find(
      (x) => String(getCustomerId(x)) === String(id)
    );

    setHeader((prev) => ({
      ...prev,
      sold_to: id,
      ship_to: c?.ship_to || c?.ShipTo || "",
      customer_number: getCustomerNumber(c),
      terms_of_payment: c?.terms || c?.Terms || ""
    }));

    setCustomerSearch(
      getCustomerName(c) || getCustomerNumber(c) || ""
    );

    setShowCustomerDropdown(false);
  };

  const handleCustomerSearchChange = (value) => {
    setCustomerSearch(value);

    const keyword = normalizeText(value);

    if (!keyword) {
      handleSelectCustomer("");
      setShowCustomerDropdown(false);
      return;
    }

    setShowCustomerDropdown(true);

    const exactCustomer = customers.find((c) => {
      const customerNumber = normalizeText(getCustomerNumber(c));
      const customerName = normalizeText(getCustomerName(c));

      return customerNumber === keyword || customerName === keyword;
    });

    if (exactCustomer) {
      handleSelectCustomer(getCustomerId(exactCustomer));
    }
  };

  const closeAllModals = () => {
    setShowStockPopup(false);
    setShowItemModal(false);
    setShowForm(false);
    setShowSaveConfirm(false);
    setShowApprovalModal(false);
    setShowApprovalConfirmModal(false);
    setSelectedSO(null);
    setPreviewImage(null);
    setShowCustomerDetailsModal(false);
    setSelectedCustomerDetails(null);

    setHeader(emptyHeader);
    setDetailItems([]);
    setItemForm(emptyItem);
    setEditItemIndex(null);

    setItemError("");
    setSaveError("");
    setCatalogSearch("");
    setCustomerSearch("");
    setCancelError("");
    setCancelLoading(false);
    setApprovalError("");
    setApprovalLoading(false);
    setApprovalRemarksInput("");
    setPendingApprovalAction("");
    setSoFiles([]);
    setAttachments([]);
    setShowDeleteAttachmentConfirm(false);
    setAttachmentToDelete(null);
    setAttachmentDeleteLoading(false);
    setAttachmentDeleteError("");

    loadData();
  };

  const orderTypeLabel = (v) => {
    const map = {
      NORMAL: "Normal",
      DEMO: "Demo",
      FREE_GOODS: "Free Goods",
      BIOCARE_ACCOUNT: "Biocare Account",
      REPLACEMENT: "Replacement",
      MARKETING_EVENT: "Marketing Event",
      ADVANCE_DELIVERY: "Advance Delivery",
      PMS: "PMS"
    };

    return map[v] || v || "-";
  };

  const deliveryModeLabel = (v) => {
    const map = {
      SINGLE: "Single",
      STAGGERED: "Staggered"
    };

    return map[v] || v || "-";
  };

  const deliveryStatusLabel = (v) => {
    const map = {
      PENDING: "Pending",
      PARTIAL: "Partial",
      COMPLETE: "Complete",
      CANCEL: "Cancelled",
      RETURNED: "Returned",
      CANCELLED: "Cancelled"
    };

    return map[normalizeFilterValue(v)] || v || "-";
  };

  const deliveryStatusClass = (v) => {
    const value = normalizeFilterValue(v);

    if (value === "COMPLETE") return "status-complete";
    if (value === "PARTIAL") return "status-partial";
    if (value === "CANCEL" || value === "CANCELLED") return "status-cancel";
    if (value === "RETURNED") return "status-returned";

    return "status-pending";
  };

  const approvalStatusLabel = (v) => {
    const map = {
      PENDING: "Pending",
      ONHOLD: "On Hold",
      APPROVED: "Approved",
      DISAPPROVED: "Disapproved"
    };

    return map[normalizeFilterValue(v)] || v || "-";
  };

  const approvalStatusClass = (v) => {
    const value = normalizeFilterValue(v);

    if (value === "APPROVED") return "status-complete";
    if (value === "DISAPPROVED") return "status-cancel";
    if (value === "ONHOLD") return "status-onhold";

    return "status-pending";
  };

  const getAttachmentUrl = (file) => {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    const API_BASE = isLocal
      ? process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
      : window.location.origin;

    let rawPath = String(
      file.file_url ||
      file.FileUrl ||
      file.file_path ||
      file.FilePath ||
      file.filePath ||
      file.url ||
      file.URL ||
      ""
    )
      .trim()
      .replace(/\\/g, "/");

    if (!rawPath) return "";

    if (rawPath.startsWith("http://localhost:5000")) {
      return isLocal
        ? rawPath
        : rawPath.replace("http://localhost:5000", window.location.origin);
    }

    if (rawPath.startsWith("http://127.0.0.1:5000")) {
      return isLocal
        ? rawPath.replace("http://127.0.0.1:5000", API_BASE)
        : rawPath.replace("http://127.0.0.1:5000", window.location.origin);
    }

    if (window.location.protocol === "https:" && rawPath.startsWith("http://")) {
      return rawPath.replace(/^http:\/\/[^/]+/i, window.location.origin);
    }

    if (rawPath.startsWith("https://")) {
      return rawPath;
    }

    if (rawPath.startsWith("/uploads")) {
      return `${API_BASE}${rawPath}`;
    }

    if (rawPath.startsWith("uploads/")) {
      return `${API_BASE}/${rawPath}`;
    }

    if (rawPath.startsWith("sales-orders/")) {
      return `${API_BASE}/uploads/${rawPath}`;
    }

    return `${API_BASE}/uploads/sales-orders/${rawPath}`;
  };

  const filteredItemsCatalog = itemsCatalog.filter((item) =>
    `${item.CatalogNo} ${item.ItemName}`
      .toLowerCase()
      .includes(catalogSearch.toLowerCase())
  );

  const filteredCustomers = customers.filter((c) =>
    `${getCustomerNumber(c)} ${getCustomerName(c)}`
      .toLowerCase()
      .includes(customerSearch.toLowerCase())
  );

  // const addItemToList = () => {
  //   setItemError("");

  //   const selected = itemsCatalog.find(
  //     (i) => String(i.ItemID) === String(itemForm.ItemId)
  //   );

  //   if (!selected) {
  //     setItemError("❌ Please select an item.");
  //     return;
  //   }

  //   const qty = parseInt(itemForm.quantity || 0, 10);

  //   if (qty <= 0) {
  //     setItemError("❌ Quantity must be greater than zero.");
  //     return;
  //   }

  //   const available = Number(selected.AvailableStock ?? 0);

  //   if (qty > available) {
  //     setStockPopupMessage(
  //       `Insufficient AVAILABLE stock (Avail: ${available}).\n\n` +
  //       `Total: ${selected.TotalStock}\n` +
  //       `Reserved: ${selected.ReservedStock}\n` +
  //       `Available: ${available}\n\n` +
  //       `Please contact LOGISTICS.`
  //     );
  //     setShowStockPopup(true);
  //     return;
  //   }

  //   const ext = qty * (parseFloat(itemForm.unit_price) || 0);

  //   const newItem = {
  //     ...itemForm,
  //     ItemId: selected.ItemID,
  //     catalog_number: selected.CatalogNo,
  //     item_name: selected.ItemName,
  //     extension_price: ext
  //   };

  //   if (editItemIndex !== null) {
  //     const updated = [...detailItems];
  //     updated[editItemIndex] = newItem;
  //     setDetailItems(updated);
  //     setEditItemIndex(null);
  //   } else {
  //     setDetailItems([...detailItems, newItem]);
  //   }

  //   setItemsCatalog((prev) =>
  //     prev.map((i) =>
  //       Number(i.ItemID) === Number(selected.ItemID)
  //         ? {
  //           ...i,
  //           AvailableStock: Number(i.AvailableStock ?? 0) - qty,
  //           ReservedStock: Number(i.ReservedStock ?? 0) + qty
  //         }
  //         : i
  //     )
  //   );

  //   setItemForm(emptyItem);
  //   setShowItemModal(false);
  // };

  const addItemToList = () => {
    setItemError("");

    const selected = itemsCatalog.find(
      (i) => String(i.ItemID) === String(itemForm.ItemId)
    );

    if (!selected) {
      setItemError("❌ Please select an item.");
      return;
    }

    const qty = parseInt(itemForm.quantity || 0, 10);

    if (qty <= 0) {
      setItemError("❌ Quantity must be greater than zero.");
      return;
    }

    const unitPrice = cleanDecimal(itemForm.unit_price);

    const ext = qty * unitPrice;

    const newItem = {
      ...itemForm,
      ItemId: selected.ItemID,
      catalog_number: selected.CatalogNo,
      item_name: selected.ItemName,
      unit_price: formatMoney(unitPrice),
      extension_price: formatMoney(ext)
    };

    if (editItemIndex !== null) {
      const updated = [...detailItems];
      updated[editItemIndex] = newItem;
      setDetailItems(updated);
      setEditItemIndex(null);
    } else {
      setDetailItems([...detailItems, newItem]);
    }

    setItemForm(emptyItem);
    setShowItemModal(false);
  };

  // const validateSalesOrderBeforeSave = () => {
  //   setSaveError("");

  //   if (!header.sold_to) {
  //     setSaveError("❌ Please select a customer before saving the Sales Order.");
  //     return false;
  //   }

  //   if (!soFiles || soFiles.length === 0) {
  //     setSaveError(
  //       "❌ Please attach at least one file before saving the Sales Order."
  //     );
  //     return false;
  //   }

  //   if (!detailItems || detailItems.length === 0) {
  //     setSaveError("❌ Please add at least one item before saving.");
  //     return false;
  //   }

  //   const requestedByItem = new Map();

  //   for (const it of detailItems) {
  //     const key = it.ItemId;
  //     const prev = requestedByItem.get(key) || 0;
  //     requestedByItem.set(key, prev + Number(it.quantity || 0));
  //   }

  //   for (const [itemId, reqQty] of requestedByItem.entries()) {
  //     const snap = initialCatalogRef.current.find(
  //       (x) => Number(x.ItemID) === Number(itemId)
  //     );

  //     const snapAvail = Number(
  //       snap?.AvailableStock ??
  //       (Number(snap?.TotalStock ?? 0) - Number(snap?.ReservedStock ?? 0))
  //     );

  //     if (reqQty > snapAvail) {
  //       setStockPopupMessage(
  //         `Cannot proceed.\n\n` +
  //         `${snap?.CatalogNo || ""} - ${snap?.ItemName || ""}\n` +
  //         `Requested: ${reqQty}\n` +
  //         `Available: ${snapAvail}\n\n` +
  //         `Please contact LOGISTICS.`
  //       );
  //       setShowStockPopup(true);
  //       return false;
  //     }
  //   }

  //   if (header.delivery_mode === "STAGGERED") {
  //     const d = Number(header.stagger_deliveries || 0);

  //     if (d < 2 || d > 12) {
  //       setSaveError("❌ Number of deliveries must be between 2 and 12.");
  //       return false;
  //     }

  //     if (!header.stagger_frequency) {
  //       setSaveError("❌ Please select a stagger frequency.");
  //       return false;
  //     }

  //     if (!header.stagger_start_month) {
  //       setSaveError("❌ Please select a start month.");
  //       return false;
  //     }
  //   }

  //   return true;
  // };

  const validateSalesOrderBeforeSave = () => {
    setSaveError("");

    if (!header.sold_to) {
      setSaveError("❌ Please select a customer before saving the Sales Order.");
      return false;
    }

    if (!soFiles || soFiles.length === 0) {
      setSaveError(
        "❌ Please attach at least one file before saving the Sales Order."
      );
      return false;
    }

    if (!detailItems || detailItems.length === 0) {
      setSaveError("❌ Please add at least one item before saving.");
      return false;
    }

    if (header.delivery_mode === "STAGGERED") {
      const d = Number(header.stagger_deliveries || 0);

      if (d < 2 || d > 12) {
        setSaveError("❌ Number of deliveries must be between 2 and 12.");
        return false;
      }

      if (!header.stagger_frequency) {
        setSaveError("❌ Please select a stagger frequency.");
        return false;
      }

      if (!header.stagger_start_month) {
        setSaveError("❌ Please select a start month.");
        return false;
      }
    }

    return true;
  };

  const confirmSaveSalesOrder = async () => {
    // Prevent duplicate clicks / double submit
    if (savingSORef.current || isSavingSO) return;

    if (!soFiles || soFiles.length === 0) {
      setSaveError("❌ Please attach at least one file before saving the Sales Order.");
      setShowSaveConfirm(false);
      return;
    }

    if (!detailItems || detailItems.length === 0) {
      setSaveError("❌ Please add at least one item before saving the Sales Order.");
      setShowSaveConfirm(false);
      return;
    }

    savingSORef.current = true;
    setIsSavingSO(true);
    setSaveError("");

    try {
      const formattedItems = detailItems.map((item) => ({
        ItemId: item.ItemId,
        catalog_number: item.catalog_number,
        item_name: item.item_name,
        quantity: parseFloat(item.quantity) || 0,
        unit_of_measure: item.unit_of_measure,
        initial_price: cleanDecimal(item.unit_price)
      }));

      const result = await addSalesOrder({
        header: {
          ...header,
          date: toMySQLDateTime(header.date),
          special_instruction: header.special_instruction || "",
          order_type: header.order_type || "NORMAL",
          delivery_mode: header.delivery_mode || "SINGLE",
          stagger_deliveries:
            header.delivery_mode === "STAGGERED"
              ? Number(header.stagger_deliveries || 2)
              : null,
          stagger_frequency:
            header.delivery_mode === "STAGGERED"
              ? header.stagger_frequency
              : null,
          stagger_start_month:
            header.delivery_mode === "STAGGERED"
              ? header.stagger_start_month
              : null
        },
        items: formattedItems
      });

      const newSOID = result?.SOID || result?.soid || result?.insertId;

      if (!newSOID) {
        throw new Error("Sales Order was saved but no SOID was returned.");
      }

      if (soFiles.length > 0) {
        for (const file of soFiles) {
          await uploadSalesOrderAttachment(newSOID, file);
        }
      }

      setShowSaveConfirm(false);
      setHeader(emptyHeader);
      setDetailItems([]);
      setShowForm(false);
      setCustomerSearch("");
      setSoFiles([]);

      await loadData();
    } catch (e) {
      setSaveError(
        e?.response?.data?.error ||
        e?.message ||
        "❌ Failed to save Sales Order. Please try again."
      );

      setShowSaveConfirm(false);
    } finally {
      savingSORef.current = false;
      setIsSavingSO(false);
    }
  };

  const resetOrderForm = () => {
    setHeader(emptyHeader);
    setDetailItems([]);
    setEditItemIndex(null);
    setSaveError("");
    setItemError("");
    setCustomerSearch("");
    setShowSaveConfirm(false);
    setSoFiles([]);
  };

  const resetItemForm = () => {
    setItemForm(emptyItem);
    setEditItemIndex(null);
    setItemError("");
  };

  const selectedSOStatus = normalizeFilterValue(
    selectedSO?.SOStatus || selectedSO?.sostatus || ""
  );

  const selectedApprovalStatus = normalizeFilterValue(
    selectedSO?.ApprovalStatus || selectedSO?.approvalstatus || ""
  );

  const isApprovalPendingLike =
    selectedApprovalStatus === "PENDING" || selectedApprovalStatus === "ONHOLD";

  const isApprovalFinalized =
    selectedApprovalStatus === "APPROVED" ||
    selectedApprovalStatus === "DISAPPROVED";

  const openApprovalModal = () => {
    if (!canReviewApproval) return;

    const currentStatus = normalizeFilterValue(
      selectedSO?.ApprovalStatus || selectedSO?.approvalstatus || "PENDING"
    );

    if (currentStatus === "PENDING" || currentStatus === "ONHOLD") {
      setApprovalRemarksInput(
        selectedSO?.PendingRemarks || selectedSO?.pendingremarks || ""
      );
    } else {
      setApprovalRemarksInput(
        selectedSO?.ApprovalRemarks || selectedSO?.approvalremarks || ""
      );
    }

    setApprovalError("");
    setShowApprovalModal(true);
  };

  const handleSavePendingRemarks = async () => {
    if (!canReviewApproval) return;
    if (!selectedSO) return;

    const currentStatus = normalizeFilterValue(
      selectedSO?.ApprovalStatus || selectedSO?.approvalstatus || "PENDING"
    );

    if (currentStatus === "APPROVED" || currentStatus === "DISAPPROVED") {
      setApprovalError(
        `Approval already finalized as ${currentStatus}. Pending remarks can no longer be updated.`
      );
      return;
    }

    try {
      setApprovalLoading(true);
      setApprovalError("");

      const soid = selectedSO.SOID || selectedSO.soid;

      await savePendingApprovalRemarks(soid, approvalRemarksInput);

      const updated = await getSalesOrder(soid);
      setSelectedSO((prev) => normalizeSOForView(updated, prev));
      await loadData();

      setApprovalError("Sales Order placed on hold successfully.");
    } catch (e) {
      setApprovalError(
        e?.response?.data?.error ||
        e?.message ||
        "Failed to place Sales Order on hold.");
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApproveSO = async () => {
    if (!canReviewApproval) return;
    if (!selectedSO) return;

    const currentStatus = normalizeFilterValue(
      selectedSO?.ApprovalStatus || selectedSO?.approvalstatus || "PENDING"
    );

    if (currentStatus === "APPROVED" || currentStatus === "DISAPPROVED") {
      setApprovalError(
        `Approval already finalized as ${currentStatus}. Status can no longer be changed.`
      );
      return;
    }

    setPendingApprovalAction("APPROVE");
    setShowApprovalConfirmModal(true);
  };

  // const handleDisapproveSO = async () => {
  //   if (!canReviewApproval) return;
  //   if (!selectedSO) return;

  //   const currentStatus = normalizeFilterValue(
  //     selectedSO?.ApprovalStatus || selectedSO?.approvalstatus || "PENDING"
  //   );

  //   if (currentStatus === "APPROVED" || currentStatus === "DISAPPROVED") {
  //     setApprovalError(
  //       `Approval already finalized as ${currentStatus}. Status can no longer be changed.`
  //     );
  //     return;
  //   }

  //   if (!String(approvalRemarksInput).trim()) {
  //     setApprovalError("Disapprove remarks are required.");
  //     return;
  //   }

  //   setPendingApprovalAction("DISAPPROVE");
  //   setShowApprovalConfirmModal(true);
  // };

  const submitApprovalAction = async () => {
    if (!canReviewApproval) return;
    if (!selectedSO || !pendingApprovalAction) return;

    try {
      setApprovalLoading(true);
      setApprovalError("");

      const soid = selectedSO.SOID || selectedSO.soid;

      if (pendingApprovalAction === "APPROVE") {
        await approveSalesOrder(soid, approvalRemarksInput);
      } else if (pendingApprovalAction === "DISAPPROVE") {
        await disapproveSalesOrder(soid, approvalRemarksInput.trim());
      }

      const updated = await getSalesOrder(soid);
      setSelectedSO((prev) => normalizeSOForView(updated, prev));
      await loadData();

      setShowApprovalConfirmModal(false);
      setShowApprovalModal(false);
      setPendingApprovalAction("");
    } catch (e) {
      setApprovalError(
        e?.response?.data?.error ||
        e?.message ||
        `Failed to ${pendingApprovalAction.toLowerCase()} Sales Order.`
      );
      setShowApprovalConfirmModal(false);
    } finally {
      setApprovalLoading(false);
    }
  };

  const downloadThreeCopiesPDF = async () => {
    if (!printRef.current) return;

    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const usableWidth = pageWidth - margin * 2;

    const copyTypes = [
      { type: "OFFICE", note: "WHITE - OFFICE FILE" },
      { type: "SALESMAN", note: "YELLOW - SALESMAN'S COPY" }
    ];

    for (let i = 0; i < copyTypes.length; i++) {
      window.dispatchEvent(
        new CustomEvent("SET_COPY_TYPE", { detail: copyTypes[i].type })
      );

      await new Promise((resolve) => setTimeout(resolve, 400));

      const element = printRef.current;
      const canvas = await html2canvas(element, { scale: 3 });
      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * usableWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = margin;

      if (i > 0) doc.addPage();

      doc.addImage(imgData, "PNG", margin, position, usableWidth, imgHeight);
      heightLeft -= pageHeight - margin * 2;

      while (heightLeft > 0) {
        doc.addPage();
        position = margin - (imgHeight - heightLeft);
        doc.addImage(imgData, "PNG", margin, position, usableWidth, imgHeight);
        heightLeft -= pageHeight - margin * 2;
      }
    }

    doc.save(`${selectedSO?.SONumber || selectedSO?.so_number || "SO"}.pdf`);
  };

  const getSOCreatedByName = (so) => {
    return (
      String(
        so.CreatedByName ??
        so.createdbyname ??
        so.CreatedBy ??
        so.createdby ??
        so.PreparedBy ??
        so.preparedby ??
        so.PendingBy ??
        so.pendingby ??
        ""
      ).trim() || "-"
    );
  };

  const getSOCreatedDateRaw = (so) => {
    return (
      so.CreatedAt ||
      so.createdat ||
      so.created_at ||
      so.DateCreated ||
      so.datecreated ||
      so.sodate ||
      so.SODate ||
      ""
    );
  };

  const getSOReferenceOrder = (so) => {
    return String(
      so?.referenceorder ??
      so?.ReferenceOrder ??
      so?.reference_order ??
      ""
    ).trim();
  };

  const createdByFilterOptions = useMemo(() => {
    const names = soList
      .map((so) => getSOCreatedByName(so))
      .filter((name) => name && name !== "-");

    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [soList]);

  const filteredSOList = useMemo(() => {
    const keyword = tableSearch.trim().toLowerCase();

    return soList.filter((so) => {
      const soNumber = so.sonumber || so.SONumber || "";
      const customer = so.customername || so.CustomerName || "";
      const shipTo = so.shipto || so.ShipTo || "";
      const referenceOrder = getSOReferenceOrder(so);

      const orderType = normalizeFilterValue(
        so.ordertype || so.OrderType || "NORMAL"
      );

      const deliveryMode = normalizeFilterValue(
        so.deliverymode || so.DeliveryMode || "SINGLE"
      );

      const deliveryStatus = normalizeFilterValue(
        so.deliverystatus || so.DeliveryStatus || "PENDING"
      );

      const approvalStatus = normalizeFilterValue(
        so.approvalstatus || so.ApprovalStatus || "PENDING"
      );

      const createdBy = getSOCreatedByName(so);
      const createdDateRaw = getSOCreatedDateRaw(so);
      const soCreatedDate = toDateOnly(createdDateRaw);
      const hasCreatedDateFilter = createdDateFrom || createdDateTo;

      const matchesSearch =
        !keyword ||
        `${soNumber} ${referenceOrder} ${customer} ${createdBy} ${shipTo} ${formatDateTime(
          createdDateRaw
        )} ${soCreatedDate} ${orderTypeLabel(orderType)} ${deliveryModeLabel(
          deliveryMode
        )} ${deliveryStatusLabel(deliveryStatus)} ${approvalStatusLabel(
          approvalStatus
        )}`
          .toLowerCase()
          .includes(keyword);

      const matchesCreatedBy =
        createdByFilter === "ALL" ||
        createdBy.toLowerCase() === createdByFilter.toLowerCase();

      const matchesCreatedDate =
        !hasCreatedDateFilter ||
        (soCreatedDate &&
          (!createdDateFrom || soCreatedDate >= createdDateFrom) &&
          (!createdDateTo || soCreatedDate <= createdDateTo));

      const matchesApprovalStatus =
        approvalStatusFilter === "ALL" || approvalStatus === approvalStatusFilter;

      const matchesDeliveryStatus =
        deliveryStatusFilter === "ALL" ||
        (deliveryStatusFilter === "CANCELLED"
          ? deliveryStatus === "CANCELLED" || deliveryStatus === "CANCEL"
          : deliveryStatus === deliveryStatusFilter);

      const matchesOrderType =
        orderTypeFilter === "ALL" || orderType === orderTypeFilter;

      const matchesDeliveryMode =
        deliveryModeFilter === "ALL" || deliveryMode === deliveryModeFilter;

      return (
        matchesSearch &&
        matchesCreatedBy &&
        matchesCreatedDate &&
        matchesApprovalStatus &&
        matchesDeliveryStatus &&
        matchesOrderType &&
        matchesDeliveryMode
      );
    });
  }, [
    soList,
    tableSearch,
    createdByFilter,
    createdDateFrom,
    createdDateTo,
    approvalStatusFilter,
    deliveryStatusFilter,
    orderTypeFilter,
    deliveryModeFilter
  ]);

  const soStatusCounts = useMemo(() => {
    return filteredSOList.reduce(
      (acc, so) => {
        const soStatus = normalizeFilterValue(
          so.SOStatus ||
          so.sostatus ||
          so.so_status ||
          "PENDING"
        );

        const deliveryStatus = normalizeFilterValue(
          so.DeliveryStatus ||
          so.deliverystatus ||
          so.delivery_status ||
          "PENDING"
        );

        if (soStatus === "PENDING") {
          acc.pending += 1;
        }

        if (soStatus === "ONGOING") {
          acc.ongoing += 1;
        }

        if (soStatus === "COMPLETE" || deliveryStatus === "COMPLETE") {
          acc.complete += 1;
        }

        if (
          soStatus === "CANCEL" ||
          soStatus === "CANCELLED" ||
          deliveryStatus === "CANCEL" ||
          deliveryStatus === "CANCELLED"
        ) {
          acc.cancelled += 1;
        }

        return acc;
      },
      {
        pending: 0,
        ongoing: 0,
        partial: 0,
        complete: 0,
        cancelled: 0
      }
    );
  }, [filteredSOList]);

  const handleExportSalesOrdersExcel = () => {
    const now = new Date();

    const excelSafe = (value) => {
      const text = String(value ?? "").trim();

      if (!text) return "-";

      // Prevent Excel formula injection if a database value starts with =, +, -, or @.
      if (/^[=+\-@]/.test(text)) {
        return `'${text}`;
      }

      return text;
    };

    const getSOField = (so, keys, fallback = "-") => {
      for (const key of keys) {
        const value = so?.[key];

        if (value !== undefined && value !== null && String(value).trim() !== "") {
          return value;
        }
      }

      return fallback;
    };

    const exportRows = filteredSOList.map((so, index) => {
      const createdDateRaw = getSOCreatedDateRaw(so);

      const orderType = normalizeFilterValue(
        so.ordertype || so.OrderType || "NORMAL"
      );

      const deliveryMode = normalizeFilterValue(
        so.deliverymode || so.DeliveryMode || "SINGLE"
      );

      const approvalStatus = normalizeFilterValue(
        so.approvalstatus || so.ApprovalStatus || "PENDING"
      );

      const deliveryStatus = normalizeFilterValue(
        so.deliverystatus || so.DeliveryStatus || "PENDING"
      );

      return {
        "#": index + 1,
        "SO Number": excelSafe(getSOField(so, ["sonumber", "SONumber"])),
        Customer: excelSafe(
          getSOField(so, ["customername", "CustomerName", "customer_name"])
        ),
        "Customer Number": excelSafe(
          getSOField(so, [
            "customernumber",
            "CustomerNumber",
            "customer_number",
            "Customer_Number"
          ])
        ),
        "Ship To": excelSafe(getSOField(so, ["shipto", "ShipTo", "ship_to"])),
        "Created By": excelSafe(getSOCreatedByName(so)),
        "Created Date": excelSafe(formatDateTime(createdDateRaw)),
        "Order Type": excelSafe(orderTypeLabel(orderType)),
        "Delivery Mode": excelSafe(deliveryModeLabel(deliveryMode)),
        "Approval Status": excelSafe(approvalStatusLabel(approvalStatus)),
        "Delivery Status": excelSafe(deliveryStatusLabel(deliveryStatus)),
        "Reference Order": excelSafe(
          getSOField(so, ["referenceorder", "ReferenceOrder", "reference_order"])
        ),
        Terms: excelSafe(
          getSOField(so, [
            "terms",
            "Terms",
            "terms_of_payment",
            "TermsOfPayment"
          ])
        ),
        "Special Instruction": excelSafe(
          getSOField(so, [
            "specialinstruction",
            "SpecialInstruction",
            "special_instruction"
          ])
        )
      };
    });

    if (exportRows.length === 0) {
      alert("No Sales Orders to export.");
      return;
    }

    const filterSummary = [
      ["BIOCARE INVENTORY MANAGEMENT SYSTEM"],
      ["Sales Orders Export"],
      [`Generated: ${formatDateTime(now)}`],
      [""],
      ["Applied Filters"],
      ["Search", tableSearch || "All"],
      ["Created By", createdByFilter === "ALL" ? "All" : createdByFilter],
      ["Created From", createdDateFrom || "All"],
      ["Created To", createdDateTo || "All"],
      [
        "Approval",
        approvalStatusFilter === "ALL"
          ? "All"
          : approvalStatusLabel(approvalStatusFilter)
      ],
      [
        "Delivery",
        deliveryStatusFilter === "ALL"
          ? "All"
          : deliveryStatusLabel(deliveryStatusFilter)
      ],
      [
        "Order Type",
        orderTypeFilter === "ALL" ? "All" : orderTypeLabel(orderTypeFilter)
      ],
      [
        "Delivery Mode",
        deliveryModeFilter === "ALL"
          ? "All"
          : deliveryModeLabel(deliveryModeFilter)
      ],
      [""]
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(filterSummary);

    XLSX.utils.sheet_add_json(worksheet, exportRows, {
      origin: "A15",
      skipHeader: false
    });

    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: 13 }
      },
      {
        s: { r: 1, c: 0 },
        e: { r: 1, c: 13 }
      }
    ];

    worksheet["!cols"] = [
      { wch: 6 },
      { wch: 18 },
      { wch: 35 },
      { wch: 18 },
      { wch: 40 },
      { wch: 24 },
      { wch: 24 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 22 },
      { wch: 20 },
      { wch: 45 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Orders");

    const fileDate = now.toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Sales_Orders_${fileDate}.xlsx`);
  };

  const totalPages = Math.max(1, Math.ceil(filteredSOList.length / rowsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedSOList = filteredSOList.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    tableSearch,
    rowsPerPage,
    createdByFilter,
    createdDateFrom,
    createdDateTo,
    approvalStatusFilter,
    deliveryStatusFilter,
    orderTypeFilter,
    deliveryModeFilter
  ]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const getVisiblePages = () => {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, safeCurrentPage - delta);
      i <= Math.min(totalPages - 1, safeCurrentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (totalPages >= 1) {
      rangeWithDots.push(1);
    }

    if (safeCurrentPage - delta > 2) {
      rangeWithDots.push("...");
    }

    range.forEach((page) => rangeWithDots.push(page));

    if (safeCurrentPage + delta < totalPages - 1) {
      rangeWithDots.push("...");
    }

    if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  const openDeleteAttachmentConfirm = (file) => {
    setAttachmentToDelete(file);
    setAttachmentDeleteError("");
    setShowDeleteAttachmentConfirm(true);
  };

  const closeDeleteAttachmentConfirm = () => {
    if (attachmentDeleteLoading) return;

    setShowDeleteAttachmentConfirm(false);
    setAttachmentToDelete(null);
    setAttachmentDeleteError("");
  };

  const confirmDeleteAttachment = async () => {
    if (!attachmentToDelete || !selectedSO) return;

    try {
      setAttachmentDeleteLoading(true);
      setAttachmentDeleteError("");

      await deleteSalesOrderAttachment(
        attachmentToDelete.id || attachmentToDelete.AttachmentID
      );

      const soid = selectedSO.SOID || selectedSO.soid;
      const files = await getSalesOrderAttachments(soid);

      setAttachments(files || []);
      setShowDeleteAttachmentConfirm(false);
      setAttachmentToDelete(null);
    } catch (e) {
      setAttachmentDeleteError(
        e?.response?.data?.error ||
        e?.message ||
        "Failed to delete attachment. Please try again."
      );
    } finally {
      setAttachmentDeleteLoading(false);
    }
  };

  const getValue = (obj, keys, fallback = "-") => {
    for (const key of keys) {
      const value = obj?.[key];

      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }

    return fallback;
  };

  const getCustomerDetailsFromSO = (so) => {
    const soCustomerId = getValue(
      so,
      ["CustomerID", "customer_id", "customerid", "SoldTo", "sold_to"],
      ""
    );

    const soCustomerName = getValue(
      so,
      ["CustomerName", "customername", "customer_name"],
      ""
    );

    const matchedCustomer =
      customers.find((c) => {
        const customerId = getValue(
          c,
          ["customer_id", "CustomerID", "CustomerId", "id"],
          ""
        );

        return (
          soCustomerId &&
          customerId &&
          String(customerId) === String(soCustomerId)
        );
      }) ||
      customers.find((c) => {
        const customerName = getValue(
          c,
          ["customer_name", "CustomerName", "customername"],
          ""
        );

        return (
          soCustomerName &&
          customerName &&
          String(customerName).trim().toLowerCase() ===
          String(soCustomerName).trim().toLowerCase()
        );
      });

    return {
      ...(matchedCustomer || {}),
      customer_name:
        getValue(matchedCustomer, ["customer_name", "CustomerName", "customername"], "") ||
        soCustomerName ||
        "-",
      customer_number:
        getValue(
          matchedCustomer,
          ["customer_number", "CustomerNumber", "Customer_Number"],
          ""
        ) ||
        getValue(so, ["CustomerNumber", "customer_number", "Customer_Number"], ""),
      ship_to:
        getValue(matchedCustomer, ["ship_to", "ShipTo", "shipto"], "") ||
        getValue(so, ["ShipTo", "shipto", "ship_to"], ""),
      terms:
        getValue(matchedCustomer, ["terms", "Terms", "terms_of_payment"], "") ||
        getValue(so, ["Terms", "terms", "terms_of_payment"], ""),
      area_manager_code:
        getValue(
          matchedCustomer,
          ["area_manager_code", "Area_Manager_Code", "AreaManagerCode"],
          ""
        ) ||
        getValue(so, ["Area_Manager_Code", "area_manager_code", "AreaManagerCode"], "")
    };
  };

  const openCustomerDetailsModal = (so) => {
    const details = getCustomerDetailsFromSO(so);

    setSelectedCustomerDetails(details);
    setShowCustomerDetailsModal(true);
  };

  const closeCustomerDetailsModal = () => {
    setShowCustomerDetailsModal(false);
    setSelectedCustomerDetails(null);
  };

  const selectedCustomer = customers.find(
    (c) => String(getCustomerId(c)) === String(header.sold_to)
  );

  const selectedCustomerName = selectedCustomer
    ? getCustomerName(selectedCustomer)
    : "";

  const totalAmount = detailItems.reduce(
    (sum, item) => sum + cleanDecimal(item.extension_price),
    0
  );

  const openImagePreview = (imageUrl) => {
    setPreviewImage(imageUrl);
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  return (
    <div className="so-container">
      <div className="so-page-header">
        <div className="so-page-title-wrap">
          <span className="so-page-kicker">Order Management</span>
          <h2>Sales Orders</h2>
          <p className="so-page-subtitle">
            Create, monitor, and review customer sales orders in one place.
          </p>
        </div>

        {canCreateSalesOrder && (
          <button
            className="btn-add"
            onClick={async () => {
              const nextSO = await getNextSONumber();
              setHeader({ ...emptyHeader, so_number: nextSO.nextNumber });
              setShowForm(true);
              setSaveError("");
              setItemError("");
              setCustomerSearch("");
              setShowSaveConfirm(false);
              setSoFiles([]);
            }}
          >
            + New Sales Order
          </button>
        )}
      </div>

      <div className="so-list-card">
        <div className="so-list-toolbar">
          <div className="so-toolbar-left">
            <div className="so-search-box">
              <input
                type="text"
                placeholder="Search SO number, reference order, customer, created by, ship to, order type, delivery mode..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
              />
            </div>

            <div className="so-record-count">
              <span className="so-record-number">{filteredSOList.length}</span>
              <span className="so-record-label">TOTAL RECORD</span>
            </div>

            <div className="so-status-summary">
              <div className="so-summary-pill so-summary-pending">
                <span>{soStatusCounts.pending}</span>
                <small>Total Pending SO</small>
              </div>

              <div className="so-summary-pill so-summary-ongoing">
                <span>{soStatusCounts.ongoing}</span>
                <small>Total Ongoing SO</small>
              </div>

              <div className="so-summary-pill so-summary-complete">
                <span>{soStatusCounts.complete}</span>
                <small>Total Complete SO</small>
              </div>

              <div className="so-summary-pill so-summary-cancelled">
                <span>{soStatusCounts.cancelled}</span>
                <small>Total Cancelled SO</small>
              </div>
            </div>

            {isRestrictedSOView && (
              <div
                style={{
                  padding: "7px 10px",
                  borderRadius: "999px",
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  fontSize: "12px",
                  fontWeight: 700,
                  border: "1px solid #bfdbfe"
                }}
              >
                Showing only your Sales Orders
              </div>
            )}
          </div>

          <div className="so-toolbar-right so-filter-controls">
            <div className="so-filter-group">
              <label>Created By</label>
              <select
                value={createdByFilter}
                onChange={(e) => setCreatedByFilter(e.target.value)}
              >
                <option value="ALL">All Created By</option>
                {createdByFilterOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="so-filter-group">
              <label>Created From</label>
              <input
                type="date"
                value={createdDateFrom}
                onChange={(e) => setCreatedDateFrom(e.target.value)}
              />
            </div>

            <div className="so-filter-group">
              <label>Created To</label>
              <input
                type="date"
                value={createdDateTo}
                onChange={(e) => setCreatedDateTo(e.target.value)}
              />
            </div>

            <div className="so-filter-group">
              <label>Order Type</label>
              <select
                value={orderTypeFilter}
                onChange={(e) => setOrderTypeFilter(e.target.value)}
              >
                <option value="ALL">All Order Type</option>
                <option value="NORMAL">Normal</option>
                <option value="DEMO">Demo</option>
                <option value="FREE_GOODS">Free Goods</option>
                <option value="BIOCARE_ACCOUNT">Biocare Account</option>
                <option value="REPLACEMENT">Replacement</option>
                <option value="MARKETING_EVENT">Marketing Event</option>
                <option value="ADVANCE_DELIVERY">Advance Delivery</option>
                <option value="PMS">PMS</option>
              </select>
            </div>

            <div className="so-filter-group">
              <label>Delivery Mode</label>
              <select
                value={deliveryModeFilter}
                onChange={(e) => setDeliveryModeFilter(e.target.value)}
              >
                <option value="ALL">All Delivery Mode</option>
                <option value="SINGLE">Single</option>
                <option value="STAGGERED">Staggered</option>
              </select>
            </div>

            <div className="so-filter-group">
              <label>Approval</label>
              <select
                value={approvalStatusFilter}
                onChange={(e) => setApprovalStatusFilter(e.target.value)}
              >
                <option value="ALL">All Approval</option>
                <option value="PENDING">Pending</option>
                <option value="ONHOLD">On Hold</option>
                <option value="APPROVED">Approved</option>
                {/* <option value="DISAPPROVED">Disapproved</option> */}
              </select>
            </div>

            <div className="so-filter-group">
              <label>Delivery</label>
              <select
                value={deliveryStatusFilter}
                onChange={(e) => setDeliveryStatusFilter(e.target.value)}
              >
                <option value="ALL">All Delivery</option>
                <option value="PENDING">Pending</option>
                <option value="PARTIAL">Partial</option>
                <option value="COMPLETE">Complete</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="RETURNED">Returned</option>
              </select>
            </div>

            <button
              type="button"
              className="so-clear-filter-btn"
              onClick={() => {
                setTableSearch("");
                setCreatedByFilter("ALL");
                setCreatedDateFrom("");
                setCreatedDateTo("");
                setApprovalStatusFilter("ALL");
                setDeliveryStatusFilter("ALL");
                setOrderTypeFilter("ALL");
                setDeliveryModeFilter("ALL");
              }}
            >
              Clear
            </button>

            <button
              type="button"
              className="so-export-excel-btn"
              onClick={handleExportSalesOrdersExcel}
              disabled={filteredSOList.length === 0}
            >
              Export Excel
            </button>

            <div className="so-filter-group">
              <label>Rows</label>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
        </div>

        <div className="so-table-wrap">
          <table className="so-table">
            <thead>
              <tr>
                <th>SO Number</th>
                <th>Reference Order</th>
                <th>Customer</th>
                <th>Created By</th>
                <th>Date</th>
                {/* <th>Ship To</th> */}
                <th>Order Type</th>
                <th>Delivery Mode</th>
                <th>Approval Status</th>
                <th>Delivery Status</th>
                <th className="th-actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {paginatedSOList.length > 0 ? (
                paginatedSOList.map((so) => {
                  const rawStatus = so.deliverystatus || so.DeliveryStatus;
                  const rawApproval = so.approvalstatus || so.ApprovalStatus;

                  return (
                    <tr key={so.soid || so.SOID}>
                      <td>
                        <div className="so-number-cell">
                          <div className="so-number-main">
                            {so.sonumber || so.SONumber}
                          </div>
                          <div className="so-number-sub">Sales Order</div>
                        </div>
                      </td>

                      <td title={getSOReferenceOrder(so) || "-"}>
                        <div className="so-number-cell">
                          <div className="so-number-main">
                            {getSOReferenceOrder(so) || "-"}
                          </div>
                          <div className="so-number-sub">Reference Order</div>
                        </div>
                      </td>

                      <td
                        className="so-customer-td"
                        title={so.customername || so.CustomerName || "-"}
                      >
                        <button
                          type="button"
                          className="so-customer-link"
                          onClick={() => openCustomerDetailsModal(so)}
                        >
                          <span className="so-customer-name">
                            {so.customername || so.CustomerName || "-"}
                          </span>
                          <span className="so-customer-view-text">
                            View customer details
                          </span>
                        </button>
                      </td>

                      <td>
                        <div className="so-created-by-cell">
                          {getSOCreatedByName(so)}
                        </div>
                      </td>

                      <td>
                        <div className="so-date-cell">
                          {formatDateTime(
                            so.CreatedAt ||
                            so.createdat ||
                            so.created_at ||
                            so.DateCreated ||
                            so.datecreated ||
                            so.sodate ||
                            so.SODate
                          )}
                        </div>
                      </td>

                      {/* <td title={so.shipto || so.ShipTo}>
                        {so.shipto || so.ShipTo || "-"}
                      </td> */}

                      <td>{orderTypeLabel(so.ordertype || so.OrderType)}</td>

                      <td>
                        {deliveryModeLabel(so.deliverymode || so.DeliveryMode)}
                      </td>

                      <td>
                        <span
                          className={`status-badge ${approvalStatusClass(
                            rawApproval
                          )}`}
                        >
                          {approvalStatusLabel(rawApproval)}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`status-badge ${deliveryStatusClass(
                            rawStatus
                          )}`}
                        >
                          {deliveryStatusLabel(rawStatus)}
                        </span>
                      </td>

                      <td>
                        <button
                          className="btn-view btn-action-primary"
                          onClick={async () => {
                            const soid = so.soid || so.SOID;

                            try {
                              const fullSO = await getSalesOrder(soid);
                              const files = await getSalesOrderAttachments(soid);

                              setSelectedSO(normalizeSOForView(fullSO));
                              setAttachments(files || []);
                              setCancelError("");
                              setCancelLoading(false);
                              setApprovalError("");
                              setApprovalLoading(false);
                              setApprovalRemarksInput("");
                            } catch (e) {
                              alert(
                                e?.response?.data?.error ||
                                e?.message ||
                                "Unable to open this Sales Order."
                              );
                            }
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10">
                    <div className="so-empty-state">
                      <div className="so-empty-icon">📄</div>
                      <div className="so-empty-title">No sales orders found</div>
                      <div className="so-empty-subtitle">
                        {isRestrictedSOView
                          ? "Only Sales Orders you created are visible for your role."
                          : "Try changing your search keyword or filters, or create a new sales order."}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="so-pagination">
          <div className="so-pagination-info">
            Showing {filteredSOList.length === 0 ? 0 : startIndex + 1} to{" "}
            {Math.min(endIndex, filteredSOList.length)} of{" "}
            {filteredSOList.length} entries
          </div>

          <div className="so-pagination-controls">
            <button
              className="page-btn"
              disabled={safeCurrentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              Prev
            </button>

            {visiblePages.map((page, index) =>
              page === "..." ? (
                <span key={`dots-${index}`} className="page-dots">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  className={`page-btn ${safeCurrentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              )
            )}

            <button
              className="page-btn"
              disabled={safeCurrentPage === totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showCustomerDetailsModal && selectedCustomerDetails && (
        <div className="modal-overlay">
          <div className="modal-content customer-details-modal">
            <div className="customer-details-header">
              <div>
                <span className="customer-details-kicker">Customer Profile</span>
                <h3>
                  {getValue(selectedCustomerDetails, [
                    "customer_name",
                    "CustomerName",
                    "customername"
                  ])}
                </h3>
                <p>Full customer information connected to this Sales Order.</p>
              </div>

              <button
                type="button"
                className="customer-details-close"
                onClick={closeCustomerDetailsModal}
              >
                ×
              </button>
            </div>

            <div className="customer-details-grid">
              <div className="customer-detail-box">
                <span>Customer Number</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "customer_number",
                    "CustomerNumber",
                    "Customer_Number"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Customer Name</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "customer_name",
                    "CustomerName",
                    "customername"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Terms</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "terms",
                    "Terms",
                    "terms_of_payment",
                    "TermsOfPayment"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Area Manager Code</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "area_manager_code",
                    "Area_Manager_Code",
                    "AreaManagerCode"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box customer-detail-full">
                <span>Ship To</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "ship_to",
                    "ShipTo",
                    "shipto"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box customer-detail-full">
                <span>Address</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "address",
                    "Address",
                    "customer_address",
                    "CustomerAddress"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Contact Person</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "contact_person",
                    "ContactPerson",
                    "Contact_Person"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Contact Number</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "contact_number",
                    "ContactNumber",
                    "contact_no",
                    "ContactNo",
                    "phone",
                    "Phone"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Email</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "email",
                    "Email",
                    "email_address",
                    "EmailAddress"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>TIN</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "tin",
                    "TIN",
                    "tax_identification_number"
                  ])}
                </strong>
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={closeCustomerDetailsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {canCreateSalesOrder && showForm && (
        <div className="modal-overlay">
          <div className="modal-content form-modal">
            <h3>Create Sales Order</h3>

            {saveError ? (
              <div
                style={{
                  background: "#ffe5e5",
                  color: "#b00020",
                  padding: 10,
                  borderRadius: 6,
                  marginBottom: 10
                }}
              >
                {saveError}
              </div>
            ) : null}

            <div className="form-section two-column">
              <div className="col">
                <div className="form-row highlight">
                  <label>SO Number</label>
                  <input value={header.so_number} disabled />
                </div>

                <div className="form-row">
                  <label>Customer</label>
                  <div className="customer-search-wrap" ref={customerDropdownRef}>
                    <input
                      type="text"
                      placeholder="🔍 Search customer name / number..."
                      value={customerSearch}
                      onChange={(e) => handleCustomerSearchChange(e.target.value)}
                      onFocus={() => {
                        if (customerSearch.trim()) {
                          setShowCustomerDropdown(true);
                        }
                      }}
                      className="customer-search-input"
                    />

                    {showCustomerDropdown && customerSearch.trim() && (
                      <div className="customer-dropdown-list">
                        {filteredCustomers.length > 0 ? (
                          filteredCustomers.map((c) => (
                            <button
                              type="button"
                              key={getCustomerId(c)}
                              className={`customer-dropdown-item ${String(header.sold_to) === String(getCustomerId(c))
                                ? "selected"
                                : ""
                                }`}
                              onClick={() => handleSelectCustomer(getCustomerId(c))}
                            >
                              <span className="customer-dropdown-name">
                                {getCustomerName(c)}
                              </span>

                              {getCustomerNumber(c) ? (
                                <span className="customer-dropdown-number">
                                  {getCustomerNumber(c)}
                                </span>
                              ) : null}
                            </button>
                          ))
                        ) : (
                          <div className="customer-dropdown-empty">
                            No customer found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <label>Ship To</label>
                  <textarea
                    value={header.ship_to}
                    onChange={(e) =>
                      setHeader({ ...header, ship_to: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Reference Order</label>
                  <input
                    value={header.reference_order}
                    onChange={(e) =>
                      setHeader({ ...header, reference_order: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Special Instruction</label>
                  <textarea
                    value={header.special_instruction}
                    onChange={(e) =>
                      setHeader({
                        ...header,
                        special_instruction: e.target.value
                      })
                    }
                    placeholder="Enter special instruction for this Sales Order..."
                    rows={4}
                  />
                </div>
              </div>

              <div className="col">
                <div className="form-row">
                  <label>Date</label>
                  <input
                    type="datetime-local"
                    value={header.date}
                    onChange={(e) =>
                      setHeader({ ...header, date: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>Terms of Payment</label>
                  <input
                    value={header.terms_of_payment}
                    onChange={(e) =>
                      setHeader({ ...header, terms_of_payment: e.target.value })
                    }
                  />
                </div>

                <div className="form-row">
                  <label>VAT Option</label>
                  <select
                    value={header.vat_option}
                    onChange={(e) =>
                      setHeader({ ...header, vat_option: e.target.value })
                    }
                  >
                    <option value="yes">With VAT (12%)</option>
                    <option value="no">VAT Inclusive</option>
                  </select>
                </div>

                <div className="form-row">
                  <label>Order Type</label>
                  <select
                    value={header.order_type}
                    onChange={(e) =>
                      setHeader({ ...header, order_type: e.target.value })
                    }
                  >
                    <option value="NORMAL">Normal</option>
                    <option value="DEMO">Demo</option>
                    <option value="FREE_GOODS">Free Goods</option>
                    <option value="BIOCARE_ACCOUNT">Biocare Account</option>
                    <option value="REPLACEMENT">Replacement</option>
                    <option value="MARKETING_EVENT">Marketing Event</option>
                    <option value="ADVANCE_DELIVERY">Advance Delivery</option>
                    <option value="PMS">PMS</option>
                  </select>
                </div>

                <div className="form-row">
                  <label>Delivery Mode</label>
                  <select
                    value={header.delivery_mode}
                    onChange={(e) => {
                      const mode = e.target.value;

                      setHeader((prev) => ({
                        ...prev,
                        delivery_mode: mode,
                        ...(mode === "SINGLE"
                          ? {
                            stagger_deliveries: "2",
                            stagger_frequency: "MONTHLY",
                            stagger_start_month: new Date()
                              .toISOString()
                              .slice(0, 7)
                          }
                          : {})
                      }));
                    }}
                  >
                    <option value="SINGLE">Single</option>
                    <option value="STAGGERED">Staggered</option>
                  </select>
                </div>

                {header.delivery_mode === "STAGGERED" && (
                  <>
                    <div className="form-row">
                      <label>Number of Deliveries</label>
                      <select
                        value={header.stagger_deliveries}
                        onChange={(e) =>
                          setHeader({
                            ...header,
                            stagger_deliveries: e.target.value
                          })
                        }
                      >
                        {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => (
                          <option key={n} value={String(n)}>
                            {n} deliveries
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-row">
                      <label>Frequency</label>
                      <select
                        value={header.stagger_frequency}
                        onChange={(e) =>
                          setHeader({
                            ...header,
                            stagger_frequency: e.target.value
                          })
                        }
                      >
                        <option value="WEEKLY">Weekly</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="QUARTERLY">Quarterly</option>
                      </select>
                    </div>

                    <div className="form-row">
                      <label>Start Month</label>
                      <input
                        type="month"
                        value={header.stagger_start_month}
                        onChange={(e) =>
                          setHeader({
                            ...header,
                            stagger_start_month: e.target.value
                          })
                        }
                      />
                    </div>
                  </>
                )}

                <div className="form-row">
                  <label>Delivery Status</label>
                  <input value={deliveryStatusLabel(header.delivery_status)} disabled />
                </div>
              </div>
            </div>

            <div className="form-row">
              <label>
                Attachment <span style={{ color: "#dc2626" }}>*</span>
              </label>

              <input
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                multiple
                required
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);

                  const oversizedFiles = newFiles.filter(
                    (file) => file.size > MAX_ATTACHMENT_SIZE_BYTES
                  );

                  if (oversizedFiles.length > 0) {
                    openAttachmentSizeModal(oversizedFiles);
                    e.target.value = "";
                    return;
                  }

                  setSaveError("");

                  setSoFiles((prev) => {
                    const merged = [...prev, ...newFiles];

                    const uniqueFiles = merged.filter(
                      (file, index, self) =>
                        index ===
                        self.findIndex(
                          (f) =>
                            f.name === file.name &&
                            f.size === file.size &&
                            f.lastModified === file.lastModified
                        )
                    );

                    return uniqueFiles;
                  });

                  e.target.value = "";
                }}
              />

              <small
                style={{
                  color: "#dc2626",
                  marginTop: "4px",
                  display: "block"
                }}
              >
                Required: Please attach at least one file. Maximum file size: 1024 KB only.
              </small>

              {soFiles.length > 0 ? (
                <div style={{ marginTop: "8px" }}>
                  {soFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${file.lastModified}-${index}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "10px",
                        marginTop: "6px"
                      }}
                    >
                      <small>{file.name}</small>

                      <button
                        type="button"
                        className="btn-delete"
                        onClick={() =>
                          setSoFiles((prev) => prev.filter((_, i) => i !== index))
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <h4>Items</h4>

            <button
              className="btn-add"
              onClick={() => {
                setShowItemModal(true);
                setItemError("");
              }}
            >
              + Add Item
            </button>

            <table className="item-table">
              <thead>
                <tr>
                  <th>Qty</th>
                  <th>U/M</th>
                  <th>Catalog</th>
                  <th>Name</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {detailItems.map((it, idx) => (
                  <tr key={idx}>
                    <td>{it.quantity}</td>
                    <td>{it.unit_of_measure}</td>
                    <td>{it.catalog_number}</td>
                    <td>{it.item_name}</td>
                    <td>{formatMoney(it.unit_price)}</td>
                    <td>{formatMoney(it.extension_price)}</td>
                    <td>
                      <button
                        className="btn-edit"
                        onClick={() => {
                          setItemForm(it);
                          setEditItemIndex(idx);
                          setShowItemModal(true);
                          setItemError("");
                        }}
                      >
                        Edit
                      </button>

                      <button
                        className="btn-delete"
                        onClick={() =>
                          setDetailItems(detailItems.filter((_, i) => i !== idx))
                        }
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="modal-actions">
              <button
                className="btn-save"
                onClick={() => {
                  const isValid = validateSalesOrderBeforeSave();
                  if (isValid) setShowSaveConfirm(true);
                }}
              >
                Save Order
              </button>

              <button
                className="btn-cancel"
                onClick={() => {
                  resetOrderForm();
                  setShowForm(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveConfirm && (
        <div className="modal-overlay">
          <div className="modal-content confirm-modal">
            <div className="confirm-header">
              <div>
                <div className="confirm-kicker">Final Review</div>
                <h3>Confirm Sales Order</h3>
                <p>
                  Please carefully verify all header details, item quantities,
                  pricing, and attachments before saving.
                  <br />
                  <br />
                  Once submitted, this Sales Order will be processed in the
                  system and may affect inventory and transactions. Modifications
                  after saving may not be allowed or may require cancellation.
                  <br />
                  <br />
                  Do you confirm that all information is correct and ready to
                  proceed?
                </p>
              </div>
            </div>

            <div className="confirm-summary-grid">
              <div className="confirm-card">
                <div className="confirm-card-title">Order Details</div>

                <div className="confirm-field">
                  <span>SO Number</span>
                  <strong>{header.so_number || "-"}</strong>
                </div>

                <div className="confirm-field">
                  <span>Date</span>
                  <strong>{formatDateTime(header.date)}</strong>
                </div>

                <div className="confirm-field">
                  <span>Customer</span>
                  <strong>{selectedCustomerName || "-"}</strong>
                </div>

                <div className="confirm-field">
                  <span>Reference Order</span>
                  <strong>{header.reference_order || "-"}</strong>
                </div>

                <div className="confirm-field">
                  <span>Terms of Payment</span>
                  <strong>{header.terms_of_payment || "-"}</strong>
                </div>

                <div className="confirm-field">
                  <span>VAT Option</span>
                  <strong>
                    {header.vat_option === "yes" ? "With VAT (12%)" : "No VAT"}
                  </strong>
                </div>

                <div className="confirm-field confirm-field-block">
                  <span>Special Instruction</span>
                  <strong>{header.special_instruction || "-"}</strong>
                </div>

                <div className="confirm-field confirm-field-block">
                  <span>Attachments</span>
                  <strong>
                    {soFiles.length > 0
                      ? soFiles.map((file) => file.name).join(", ")
                      : "-"}
                  </strong>
                </div>
              </div>

              <div className="confirm-card">
                <div className="confirm-card-title">Delivery Details</div>

                <div className="confirm-field">
                  <span>Order Type</span>
                  <strong>{orderTypeLabel(header.order_type)}</strong>
                </div>

                <div className="confirm-field">
                  <span>Delivery Mode</span>
                  <strong>{deliveryModeLabel(header.delivery_mode)}</strong>
                </div>

                <div className="confirm-field">
                  <span>Delivery Status</span>
                  <strong>{deliveryStatusLabel(header.delivery_status)}</strong>
                </div>

                {header.delivery_mode === "STAGGERED" && (
                  <>
                    <div className="confirm-field">
                      <span>No. of Deliveries</span>
                      <strong>{header.stagger_deliveries || "-"}</strong>
                    </div>

                    <div className="confirm-field">
                      <span>Frequency</span>
                      <strong>{header.stagger_frequency || "-"}</strong>
                    </div>

                    <div className="confirm-field">
                      <span>Start Month</span>
                      <strong>{header.stagger_start_month || "-"}</strong>
                    </div>
                  </>
                )}

                <div className="confirm-field confirm-field-block">
                  <span>Ship To</span>
                  <strong>{header.ship_to || "-"}</strong>
                </div>
              </div>
            </div>

            <div className="confirm-items-card">
              <div className="confirm-items-head">
                <div>
                  <div className="confirm-card-title">Items to Save</div>
                  <div className="confirm-items-subtitle">
                    {detailItems.length} item
                    {detailItems.length !== 1 ? "s" : ""} ready for saving
                  </div>
                </div>

                <div className="confirm-total-box">
                  <span>Grand Total</span>
                  <strong>{formatMoney(totalAmount)}</strong>
                </div>
              </div>

              <table className="item-table confirm-item-table">
                <thead>
                  <tr>
                    <th>Qty</th>
                    <th>U/M</th>
                    <th>Catalog</th>
                    <th>Name</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>

                <tbody>
                  {detailItems.map((it, idx) => (
                    <tr key={idx}>
                      <td>{it.quantity}</td>
                      <td>{it.unit_of_measure}</td>
                      <td>{it.catalog_number}</td>
                      <td>{it.item_name}</td>
                      <td>{formatMoney(it.unit_price)}</td>
                      <td>{formatMoney(it.extension_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-save"
                onClick={confirmSaveSalesOrder}
                disabled={isSavingSO}
              >
                {isSavingSO ? "Saving..." : "Confirm Save"}
              </button>

              <button
                className="btn-cancel"
                onClick={() => setShowSaveConfirm(false)}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedSO && (
        <div className="modal-overlay">
          <div className="modal-content">
            {cancelError ? (
              <div
                style={{
                  background: "#ffe5e5",
                  color: "#b00020",
                  padding: 10,
                  borderRadius: 6,
                  marginBottom: 10
                }}
              >
                {cancelError}
              </div>
            ) : null}

            {approvalError ? (
              <div
                style={{
                  background: "#fff4e5",
                  color: "#9a4d00",
                  padding: 10,
                  borderRadius: 6,
                  marginBottom: 10
                }}
              >
                {approvalError}
              </div>
            ) : null}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginBottom: 14,
                padding: 12,
                border: "1px solid #e8e8e8",
                borderRadius: 10,
                background: "#fafafa"
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  Approval Status
                </div>
                <div style={{ marginTop: 6 }}>
                  <span
                    className={`status-badge ${approvalStatusClass(
                      selectedApprovalStatus
                    )}`}
                  >
                    {approvalStatusLabel(selectedApprovalStatus)}
                  </span>
                </div>
              </div>

              {isApprovalPendingLike ? (
                <>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>Pending By</div>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      {selectedSO?.PendingBy || selectedSO?.pendingby || "-"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>Pending At</div>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      {selectedSO?.PendingAt || selectedSO?.pendingat
                        ? new Date(
                          selectedSO?.PendingAt || selectedSO?.pendingat
                        ).toLocaleString()
                        : "-"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Pending Remarks
                    </div>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      {selectedSO?.PendingRemarks ||
                        selectedSO?.pendingremarks ||
                        "-"}
                    </div>
                  </div>
                </>
              ) : selectedApprovalStatus === "DISAPPROVED" ? (
                <>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Disapproved By
                    </div>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      {selectedSO?.DisapprovedBy || "-"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Disapproved At
                    </div>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      {selectedSO?.DisapprovedAt
                        ? new Date(selectedSO.DisapprovedAt).toLocaleString()
                        : "-"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Disapproval Remarks
                    </div>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      {selectedSO?.ApprovalRemarks || "-"}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>Approved By</div>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      {selectedSO?.ApprovedBy || "-"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>Approved At</div>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      {selectedSO?.ApprovedAt
                        ? new Date(selectedSO.ApprovedAt).toLocaleString()
                        : "-"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Approval Remarks
                    </div>
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      {selectedSO?.ApprovalRemarks || "-"}
                    </div>
                  </div>
                </>
              )}

              <div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  Special Instruction
                </div>
                <div style={{ marginTop: 6, fontWeight: 600 }}>
                  {selectedSO?.SpecialInstruction ||
                    selectedSO?.special_instruction ||
                    "-"}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#666" }}>Prepared By</div>
                <div style={{ marginTop: 6, fontWeight: 600 }}>
                  {selectedSO?.PreparedBy || selectedSO?.preparedby || "-"}
                </div>
              </div>
            </div>

            <div ref={printRef}>
              <SoPrintTemplate so={selectedSO} />
            </div>

            <div className="so-attachments">
              <h4>Attachments</h4>

              {attachments.length === 0 ? (
                <div className="so-attachments-empty">
                  No attachments uploaded.
                </div>
              ) : (
                attachments.map((file) => {
                  const fileName = file.file_name || file.FileName || "";
                  const fileUrl = getAttachmentUrl(file);

                  const isImage =
                    /^image\//i.test(file.file_type || file.FileType || "") ||
                    /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);

                  const isPDF =
                    /pdf/i.test(file.file_type || file.FileType || "") ||
                    /\.pdf$/i.test(fileName);

                  return (
                    <div
                      key={file.id || file.AttachmentID}
                      className="so-attachment-item"
                    >
                      {isImage ? (
                        <button
                          type="button"
                          className="so-attachment-link so-attachment-button"
                          onClick={() => {
                            openImagePreview(fileUrl);
                          }}
                        >
                          <span className="so-attachment-icon">🖼️</span>
                          {fileName}
                        </button>
                      ) : isPDF ? (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="so-attachment-link"
                        >
                          <span className="so-attachment-icon">📄</span>
                          {fileName} (Open PDF)
                        </a>
                      ) : (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="so-attachment-link"
                        >
                          <span className="so-attachment-icon">📎</span>
                          {fileName}
                        </a>
                      )}

                      <button
                        type="button"
                        className="btn-delete"
                        onClick={() => openDeleteAttachmentConfirm(file)}
                      >
                        Delete
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-save" onClick={downloadThreeCopiesPDF}>
                Download PDF
              </button>

              {canReviewApproval && (
                <button
                  className="btn-edit"
                  disabled={
                    approvalLoading ||
                    isApprovalFinalized ||
                    selectedSOStatus === "CANCEL" ||
                    selectedSOStatus === "CANCELLED" ||
                    selectedSOStatus === "COMPLETE"
                  }
                  onClick={openApprovalModal}
                >
                  Review Approval
                </button>
              )}

              {canCancelSalesOrder && (
                <button
                  className="btn-danger"
                  disabled={
                    cancelLoading ||
                    selectedSOStatus === "CANCEL" ||
                    selectedSOStatus === "CANCELLED" ||
                    selectedSOStatus === "COMPLETE"
                  }
                  onClick={async () => {
                    if (!selectedSO) return;

                    const soid = selectedSO.SOID || selectedSO.soid;

                    const ok = window.confirm(
                      "Are you sure you want to CANCEL this Sales Order?\n\nThis is only allowed within 7 days from creation."
                    );

                    if (!ok) return;

                    try {
                      setCancelLoading(true);
                      setCancelError("");

                      await cancelSalesOrder(soid);

                      const updated = await getSalesOrder(soid);
                      setSelectedSO((prev) => normalizeSOForView(updated, prev));

                      await loadData();
                    } catch (e) {
                      const msg =
                        e?.response?.data?.error ||
                        e?.message ||
                        "❌ Cancel failed. SO may be more than 7 days old.";
                      setCancelError(msg);
                    } finally {
                      setCancelLoading(false);
                    }
                  }}
                >
                  {cancelLoading ? "Cancelling..." : "Cancel SO"}
                </button>
              )}

              <button
                className="btn-cancel"
                onClick={() => {
                  setSelectedSO(null);
                  setCancelError("");
                  setCancelLoading(false);
                  setApprovalError("");
                  setApprovalLoading(false);
                  setApprovalRemarksInput("");
                  setShowApprovalConfirmModal(false);
                  setPendingApprovalAction("");
                  setAttachments([]);
                  setPreviewImage(null);
                  setShowDeleteAttachmentConfirm(false);
                  setAttachmentToDelete(null);
                  setAttachmentDeleteLoading(false);
                  setAttachmentDeleteError("");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {canReviewApproval && showApprovalModal && selectedSO && (
        <div className="modal-overlay">
          <div className="modal-content small-modal">
            <h3>Review Sales Order Approval</h3>

            <div className="form-row">
              <label>Current Status</label>
              <input
                value={approvalStatusLabel(
                  selectedSO?.ApprovalStatus || selectedSO?.approvalstatus
                )}
                disabled
              />
            </div>

            {isApprovalFinalized && (
              <div
                style={{
                  background: "#eef6ff",
                  color: "#1f4f82",
                  padding: 10,
                  borderRadius: 6,
                  marginBottom: 10
                }}
              >
                Approval already finalized as{" "}
                {approvalStatusLabel(selectedApprovalStatus)}. This status can
                no longer be changed.
              </div>
            )}

            <div className="form-row">
              <label>Remarks</label>
              <textarea
                value={approvalRemarksInput}
                onChange={(e) => setApprovalRemarksInput(e.target.value)}
                placeholder={
                  isApprovalPendingLike
                    ? "Enter pending/on-hold remarks..."
                    : "Enter approval or disapproval remarks..."
                }
                rows={4}
                disabled={isApprovalFinalized}
              />
            </div>

            {approvalError ? (
              <div
                style={{
                  background: "#fff4e5",
                  color: "#9a4d00",
                  padding: 10,
                  borderRadius: 6,
                  marginBottom: 10
                }}
              >
                {approvalError}
              </div>
            ) : null}

            <div className="modal-actions">
              <button
                className="btn-edit"
                disabled={approvalLoading || isApprovalFinalized}
                onClick={handleSavePendingRemarks}
              >
                {approvalLoading ? "Saving..." : "Save as On Hold"}
              </button>

              <button
                className="btn-save"
                disabled={approvalLoading || isApprovalFinalized}
                onClick={handleApproveSO}
              >
                {approvalLoading ? "Processing..." : "Approve"}
              </button>

              {/* Enable this if you want disapprove button back */}
              {/* <button
                className="btn-danger"
                disabled={approvalLoading || isApprovalFinalized}
                onClick={handleDisapproveSO}
              >
                {approvalLoading ? "Processing..." : "Disapprove"}
              </button> */}

              <button
                className="btn-cancel"
                onClick={() => {
                  setShowApprovalModal(false);
                  setShowApprovalConfirmModal(false);
                  setPendingApprovalAction("");
                  setApprovalError("");
                  setApprovalRemarksInput("");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {canReviewApproval && showApprovalConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content small-modal approval-confirm-modal">
            <div className="approval-confirm-header">
              <h3>
                {pendingApprovalAction === "APPROVE"
                  ? "Confirm Approval"
                  : "Confirm Disapproval"}
              </h3>

              <p>
                {pendingApprovalAction === "APPROVE"
                  ? "Are you sure you want to APPROVE this Sales Order?"
                  : "Are you sure you want to DISAPPROVE this Sales Order?"}
              </p>

              <div className="approval-confirm-warning">
                This action cannot be changed once submitted.
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-save"
                disabled={approvalLoading}
                onClick={submitApprovalAction}
              >
                {approvalLoading ? "Processing..." : "Yes, Confirm"}
              </button>

              <button
                className="btn-cancel"
                disabled={approvalLoading}
                onClick={() => {
                  setShowApprovalConfirmModal(false);
                  setPendingApprovalAction("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {attachmentSizeModal.show && (
        <div className="modal-overlay">
          <div className="modal-content small-modal attachment-size-modal">
            <div className="attachment-size-icon">!</div>

            <h3>Attachment Too Large</h3>

            <p className="attachment-size-message">
              The selected file exceeds the allowed upload size.
            </p>

            <div className="attachment-size-limit">
              Maximum allowed size: <strong>{MAX_ATTACHMENT_SIZE_KB} KB only</strong>
            </div>

            <div className="attachment-size-file-list">
              {attachmentSizeModal.files.map((file, index) => (
                <div className="attachment-size-file" key={`${file.name}-${index}`}>
                  <span className="attachment-size-file-name">{file.name}</span>
                  <strong>{file.size}</strong>
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-save"
                onClick={closeAttachmentSizeModal}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {showItemModal && (
        <div className="modal-overlay">
          <div className="modal-content small-modal">
            <h4>{editItemIndex !== null ? "Edit Item" : "Add Item"}</h4>

            {itemError ? (
              <div
                style={{
                  background: "#ffe5e5",
                  color: "#b00020",
                  padding: 10,
                  borderRadius: 6,
                  marginBottom: 10
                }}
              >
                {itemError}
              </div>
            ) : null}

            <div className="form-row">
              <div style={{ marginBottom: "8px" }}>
                <input
                  type="text"
                  placeholder="🔍 Search Catalog or Name..."
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                    fontSize: "14px"
                  }}
                />
              </div>

              <label>Catalog</label>

              <select
                value={itemForm.ItemId}
                onChange={(e) => {
                  const selected = itemsCatalog.find(
                    (x) => String(x.ItemID) === String(e.target.value)
                  );

                  if (!selected) return;

                  setItemForm({
                    ...itemForm,
                    ItemId: selected.ItemID,
                    catalog_number: selected.CatalogNo,
                    item_name: selected.ItemName,
                    unit_of_measure:
                      selected.Unit || selected.UOM || selected.UnitOfMeasure || ""
                  });
                }}
              >
                <option value="">-- Select Item --</option>

                {filteredItemsCatalog.map((item) => (
                  <option key={item.ItemID} value={item.ItemID}>
                    {item.CatalogNo} - {item.ItemName} (
                    {item.SupplierName || "No Supplier"}) (Total: {" "}
                    {item.TotalStock} | Reserved: {item.ReservedStock} | Avail:{" "}
                    {item.AvailableStock})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label>Quantity</label>
              <input
                type="number"
                value={itemForm.quantity}
                onChange={(e) =>
                  setItemForm({ ...itemForm, quantity: e.target.value })
                }
              />
            </div>

            <div className="form-row">
              <label>U/M</label>
              <input
                type="text"
                value={itemForm.unit_of_measure}
                onChange={(e) =>
                  setItemForm({ ...itemForm, unit_of_measure: e.target.value })
                }
              />
            </div>

            <div className="form-row">
              <label>Unit Price</label>
              <input
                type="text"
                inputMode="decimal"
                value={itemForm.unit_price}
                onChange={(e) => {
                  const value = e.target.value;

                  // allow numbers, comma, and decimal point only
                  if (/^[0-9,]*\.?[0-9]*$/.test(value)) {
                    setItemForm({ ...itemForm, unit_price: value });
                  }
                }}
                placeholder="Example: 1,500.00"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-save" onClick={addItemToList}>
                {editItemIndex !== null ? "Update" : "Add"}
              </button>

              <button
                className="btn-cancel"
                onClick={() => {
                  resetItemForm();
                  setShowItemModal(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showStockPopup && (
        <div className="modal-overlay stockpop-overlay">
          <div className="stockpop-card">
            <div className="stockpop-header">
              <div className="stockpop-icon">!</div>

              <div>
                <div className="stockpop-title">Insufficient Stock</div>
                <div className="stockpop-subtitle">
                  You requested more than the available quantity.
                </div>
              </div>
            </div>

            {/* <div className="stockpop-body">
              <div className="stockpop-message" style={{ whiteSpace: "pre-line" }}>
                {stockPopupMessage}
              </div>
            </div> */}

            <div className="stockpop-footer">
              <button className="btn-danger" onClick={closeAllModals}>
                Contact Logistics
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteAttachmentConfirm && attachmentToDelete && (
        <div className="modal-overlay">
          <div className="modal-content small-modal approval-confirm-modal">
            <div className="approval-confirm-header">
              <h3>Delete Attachment</h3>
              <p>Are you sure you want to delete this attachment?</p>

              <div className="approval-confirm-warning">
                This action cannot be undone.
              </div>

              <div style={{ marginTop: 12, fontWeight: 600 }}>
                {attachmentToDelete.file_name ||
                  attachmentToDelete.FileName ||
                  "Selected attachment"}
              </div>
            </div>

            {attachmentDeleteError ? (
              <div
                style={{
                  background: "#ffe5e5",
                  color: "#b00020",
                  padding: 10,
                  borderRadius: 6,
                  marginBottom: 10
                }}
              >
                {attachmentDeleteError}
              </div>
            ) : null}

            <div className="modal-actions">
              <button
                className="btn-danger"
                disabled={attachmentDeleteLoading}
                onClick={confirmDeleteAttachment}
              >
                {attachmentDeleteLoading ? "Deleting..." : "Yes, Delete"}
              </button>

              <button
                className="btn-cancel"
                disabled={attachmentDeleteLoading}
                onClick={closeDeleteAttachmentConfirm}
              >
                No, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div className="modal-overlay image-preview-overlay" onClick={closeImagePreview}>
          <div
            className="modal-content image-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="image-preview-header">
              <div>
                <span className="image-preview-kicker">Attachment Preview</span>
                <h4>Image Viewer</h4>
              </div>

              <button
                type="button"
                className="image-preview-close"
                onClick={closeImagePreview}
              >
                ×
              </button>
            </div>

            <div className="image-preview-toolbar">
              <a
                href={previewImage}
                target="_blank"
                rel="noreferrer"
                className="image-open-original"
              >
                Open Original
              </a>
            </div>

            <div className="image-preview-body">
              <div className="image-preview-canvas">
                <img
                  src={previewImage}
                  alt="Attachment Preview"
                  className="preview-image"
                  onError={() => {
                    console.error("Failed image URL:", previewImage);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
